import { readonly, ref } from "vue";
import type { DashboardTheme } from "./dashboard.types.ts";

const DASHBOARD_THEME_STORAGE_KEY = "insight-iq-theme";

function isDashboardTheme(value: unknown): value is DashboardTheme {
  return value === "dark" || value === "light";
}

function preferredTheme(): DashboardTheme {
  if (
    typeof globalThis.matchMedia === "function" &&
    globalThis.matchMedia("(prefers-color-scheme: light)").matches
  ) {
    return "light";
  }

  return "dark";
}

function readStoredTheme(): DashboardTheme | null {
  try {
    const value = globalThis.localStorage.getItem(DASHBOARD_THEME_STORAGE_KEY);
    return isDashboardTheme(value) ? value : null;
  } catch {
    return null;
  }
}

function readInitialTheme(): DashboardTheme {
  const documentTheme = globalThis.document.documentElement.dataset.theme;
  if (isDashboardTheme(documentTheme)) {
    return documentTheme;
  }

  return readStoredTheme() ?? preferredTheme();
}

const theme = ref<DashboardTheme>(readInitialTheme());

function applyTheme(nextTheme: DashboardTheme): void {
  theme.value = nextTheme;

  if (typeof document !== "undefined") {
    document.documentElement.dataset.theme = nextTheme;
  }

  try {
    globalThis.localStorage.setItem(DASHBOARD_THEME_STORAGE_KEY, nextTheme);
  } catch {
    // Ignore storage failures and keep the in-memory theme active.
  }
}

function initializeDashboardTheme(): void {
  const initialTheme = readInitialTheme();
  theme.value = initialTheme;

  if (typeof document !== "undefined") {
    document.documentElement.dataset.theme = initialTheme;
  }
}

export function useDashboardTheme() {
  return {
    theme: readonly(theme),
    initializeDashboardTheme,
    setTheme: applyTheme,
    toggleTheme() {
      applyTheme(theme.value === "dark" ? "light" : "dark");
    },
  };
}
